import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { apiRequest } from "../../../services/apiService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SurveyListHeader from "../../../component/common/layout/SurveyListHeader";
import SurveyListSidebar from "../../../component/common/layout/SurveyListSidebar";
import AccountSettingSidebar from "../../../component/common/layout/AccountSettingSidebar";
import NestedMenuToggle from "./NestedMenuToggle";

const SecuritySettings = () => {
  const { t } = useTranslation();
  const [mfaEnabled, setMfaEnabled] = useState(true);
  const [accountLockEnabled, setAccountLockEnabled] = useState(true);
  const [maxAttempts, setMaxAttempts] = useState(5);
  const [lockDuration, setLockDuration] = useState(30);
  const [passwordExpiryDays, setPasswordExpiryDays] = useState(90);
  const [ssoEnabled, setSsoEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { response } = await apiRequest({
        method: "GET",
        url: "/security-settings",
      });
      if (response?.data?.data) {
        const d = response.data.data;
        setMfaEnabled(d.mfa_enabled == 1);
        setAccountLockEnabled(d.account_lock_enabled == 1);
        setMaxAttempts(d.max_login_attempts || 5);
        setLockDuration(d.lock_duration_minutes || 30);
        setPasswordExpiryDays(d.password_expiry_days != null ? d.password_expiry_days : 90);
        setSsoEnabled(d.sso_enabled != null ? d.sso_enabled == 1 : true);
      }
    } catch (e) {
      console.error("Error loading security settings:", e);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { response, error } = await apiRequest({
        method: "PUT",
        url: "/security-settings",
        showErrorToast: false,
        data: {
          mfa_enabled: mfaEnabled,
          account_lock_enabled: accountLockEnabled,
          max_login_attempts: maxAttempts,
          lock_duration_minutes: lockDuration,
          password_expiry_days: passwordExpiryDays,
          sso_enabled: ssoEnabled,
        },
      });
      if (response?.data?.statusCode === 200) {
        toast.success(t('settings.security_settings.success'));
      } else {
        toast.error(error?.response?.data?.msg || t('settings.security_settings.error'));
      }
    } catch (e) {
      toast.error(t('settings.security_settings.error'));
    }
    setSaving(false);
  };

  const cardStyle = (enabled) => ({
    border: enabled ? "2px solid #e60000" : "1px solid #dee2e6",
    borderRadius: "12px",
    padding: "24px",
    background: enabled ? "#fff5f5" : "#fff",
    transition: "all 0.2s ease",
    marginBottom: "20px",
  });

  const toggleStyle = {
    position: "relative",
    width: "48px",
    height: "26px",
    cursor: "pointer",
    flexShrink: 0,
  };

  const toggleTrack = (on) => ({
    position: "absolute",
    top: 0, left: 0, right: 0, bottom: 0,
    borderRadius: "13px",
    background: on ? "#e60000" : "#ccc",
    transition: "background 0.2s",
  });

  const toggleThumb = (on) => ({
    position: "absolute",
    top: "3px",
    left: on ? "25px" : "3px",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    background: "#fff",
    transition: "left 0.2s",
    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
  });

  return (
    <>
      <div className="d-flex flex-column main-outer-wrapper main-site-wrapper bg-white">
        <SurveyListHeader />
        <div id="viewport" className="d-flex bg-white">
          <SurveyListSidebar />
          <div className="flex-grow-1 d-flex">
            <AccountSettingSidebar />
            <div className="flex-grow-1 page-content-wrap">
              <div className="page-heading">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/admin/settings/profile">{t('settings.title')}</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/admin/settings/profile">{t('settings.general_breadcrumb')}</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      <strong>{t('settings.security')}</strong>
                    </li>
                  </ol>
                </nav>
                <div className="d-flex align-items-center">
                  <NestedMenuToggle />
                  <div className="flex-grow-1 ps-3 ps-lg-0">
                    <h1>{t('settings.security_settings.title')}</h1>
                  </div>
                </div>
              </div>

              <div className="profile-wrapper">
                <p style={{ color: "#666", fontSize: "14px", margin: "0 0 24px 0" }}>
                  {t('settings.security_settings.description')}
                </p>

                {loading ? (
                  <div className="text-center py-5">
                    <span className="spinner-border spinner-border-sm" role="status"></span>
                    <span className="ms-2">{t('settings.loading_settings')}</span>
                  </div>
                ) : (
                  <>
                    <div style={cardStyle(ssoEnabled)}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                            <i className="fa fa-windows" style={{ color: ssoEnabled ? "#e60000" : "#666", fontSize: "18px" }}></i>
                            <span style={{ fontWeight: "600", fontSize: "16px", color: "#333" }}>
                              {t('settings.security_settings.sso_title')}
                            </span>
                          </div>
                          <p style={{ color: "#666", fontSize: "13px", margin: 0, lineHeight: "1.6" }}>
                            {t('settings.security_settings.sso_description')}
                          </p>
                        </div>
                        <div style={toggleStyle} onClick={() => setSsoEnabled(!ssoEnabled)}>
                          <div style={toggleTrack(ssoEnabled)}></div>
                          <div style={toggleThumb(ssoEnabled)}></div>
                        </div>
                      </div>
                    </div>

                    <div style={cardStyle(mfaEnabled)}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                            <i className="fa fa-shield" style={{ color: mfaEnabled ? "#e60000" : "#666", fontSize: "18px" }}></i>
                            <span style={{ fontWeight: "600", fontSize: "16px", color: "#333" }}>
                              {t('settings.security_settings.mfa_title')}
                            </span>
                          </div>
                          <p style={{ color: "#666", fontSize: "13px", margin: 0, lineHeight: "1.6" }}>
                            {t('settings.security_settings.mfa_description')}
                          </p>
                        </div>
                        <div style={toggleStyle} onClick={() => setMfaEnabled(!mfaEnabled)}>
                          <div style={toggleTrack(mfaEnabled)}></div>
                          <div style={toggleThumb(mfaEnabled)}></div>
                        </div>
                      </div>
                    </div>

                    <div style={cardStyle(accountLockEnabled)}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                            <i className="fa fa-lock" style={{ color: accountLockEnabled ? "#e60000" : "#666", fontSize: "18px" }}></i>
                            <span style={{ fontWeight: "600", fontSize: "16px", color: "#333" }}>
                              {t('settings.security_settings.lockout_title')}
                            </span>
                          </div>
                          <p style={{ color: "#666", fontSize: "13px", margin: 0, lineHeight: "1.6", marginBottom: "16px" }}>
                            {t('settings.security_settings.lockout_description')}
                          </p>
                          {accountLockEnabled && (
                            <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                              <div>
                                <label style={{ fontSize: "12px", fontWeight: "600", color: "#555", display: "block", marginBottom: "6px" }}>
                                  {t('settings.security_settings.max_attempts')}
                                </label>
                                <select
                                  value={maxAttempts}
                                  onChange={(e) => setMaxAttempts(Number(e.target.value))}
                                  style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid #ddd", fontSize: "14px", minWidth: "80px", outline: "none" }}
                                >
                                  {[3, 5, 7, 10].map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label style={{ fontSize: "12px", fontWeight: "600", color: "#555", display: "block", marginBottom: "6px" }}>
                                  {t('settings.security_settings.lock_duration')}
                                </label>
                                <select
                                  value={lockDuration}
                                  onChange={(e) => setLockDuration(Number(e.target.value))}
                                  style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid #ddd", fontSize: "14px", minWidth: "120px", outline: "none" }}
                                >
                                  {[15, 30, 60, 120].map((n) => (
                                    <option key={n} value={n}>{n} {t('settings.security_settings.minutes')}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          )}
                        </div>
                        <div style={toggleStyle} onClick={() => setAccountLockEnabled(!accountLockEnabled)}>
                          <div style={toggleTrack(accountLockEnabled)}></div>
                          <div style={toggleThumb(accountLockEnabled)}></div>
                        </div>
                      </div>
                    </div>

                    <div style={cardStyle(passwordExpiryDays > 0)}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                            <i className="fa fa-calendar" style={{ color: passwordExpiryDays > 0 ? "#e60000" : "#666", fontSize: "18px" }}></i>
                            <span style={{ fontWeight: "600", fontSize: "16px", color: "#333" }}>
                              {t('settings.security_settings.password_expiry_title')}
                            </span>
                          </div>
                          <p style={{ color: "#666", fontSize: "13px", margin: 0, lineHeight: "1.6", marginBottom: "16px" }}>
                            {t('settings.security_settings.password_expiry_description')}
                          </p>
                          <div>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#555", display: "block", marginBottom: "6px" }}>
                              {t('settings.security_settings.expire_after')}
                            </label>
                            <select
                              value={passwordExpiryDays}
                              onChange={(e) => setPasswordExpiryDays(Number(e.target.value))}
                              style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid #ddd", fontSize: "14px", minWidth: "150px", outline: "none" }}
                            >
                              <option value={0}>{t('settings.security_settings.disabled')}</option>
                              <option value={30}>30 {t('settings.security_settings.days')}</option>
                              <option value={60}>60 {t('settings.security_settings.days')}</option>
                              <option value={90}>90 {t('settings.security_settings.days')}</option>
                              <option value={120}>120 {t('settings.security_settings.days')}</option>
                              <option value={180}>180 {t('settings.security_settings.days')}</option>
                              <option value={365}>365 {t('settings.security_settings.days')}</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>

                    {window.modulePermissions?.M9?.edit == 1 && (
                    <div className="btn-wrap">
                      <button
                        className="formsbutton btn-bordered btn"
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <span className="white-spinner spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            <span className="visually-hidden">{t('settings.loading')}</span>
                          </>
                        ) : (
                          t('settings.save_changes')
                        )}
                      </button>
                    </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SecuritySettings;
